var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { AskMessage, AxisHelperFacet, ChangeInputDeviceButtonMappingMessage, ControllerHand, convertToNumber3, convertToNumber4, CustomControllerFacet, EnableControllerAnnotationMessage, EnableInputDevicesMessage, InputDeviceType, ModuleId, PendingMessages, RecordingIndicator, RemoveFacetFromModelMessage, RemoveSubModelMessage, RequestControllerTransformMessage, ResponseControllerTransformMessage, ROOT_MODEL_UID, UpdateFacetOnModelMessage, ButtonType, EnableControllerJoystickMessage } from "flavr-common";
import { Quaternion, Vector3 } from "flavr-math";
import { AudioVolumeMessage, ControllerConnectedMessage, ControllerDisconnectedMessage, ControllerHapticFeedback, ControllerSelectionRayVisibilityMessage, ControllerTrackingChangedMessage, ControllerVisibilityMessage, MouseConnectedMessage, ResetControllerButtonDescription, SetControllerButtonDescription } from "@messages/controller/ControllerMessages";
import { SetSelectionStrategyForHandsMessage, SetSelectionStrategyMessage } from "@messages/selection/SelectionStrategyMessages";
import { UpdatePlatformTransformMessage } from "@messages/locomotion/LocomotionMessages";
import { EndSessionMessage } from "@messages/session/SessionMessages";
import { SetSkyboxRotation } from "@messages/rendering/MeshMessages";
import { SELECTION_STRATEGY } from "@messages/selection";
import { animationFrame$ } from "@messages/xr/AnimationFrame";
import { assertIsWeb } from "@messages/environment";
import { WebXRPresentingMessage } from "@messages/xr/WebXRMessages";
import { busService } from "@modules/bus";
import { preferencesService } from "@modules/preferences/PreferencesService";
import { localPlayerContainer } from "@modules/localplayer/LocalPlayerContainer";
import { usersService } from "@modules/multiplayer/UsersService";
import { modelPool, useExternalMessages } from "@modules/model";
import { ensureLocalPlayerControllersModel, getLocalPlayerClientIdxOrThrow, getPlayerControllerModelName, getPlayerMouseModelName, hasLocalPlayerClientIdx } from "@modules/player";
import { Module } from "@modules/module";
import { gamepadDetectionService } from "./GamepadDetectionService";
import { ControllerInputDevice } from "./ControllerInputDevice";
import { controllerDeviceService } from "./ControllerDeviceService";
import { getControllerGripSpaceModel } from "./ControllerGripSpace";
import { getControllerMeshInfo } from "./getControllerMeshInfo";
import { MouseInputDevice } from "../devices/MouseInputDevice";
import { KeyboardInputDevice } from "../devices/KeyboardInputDevice";
import { BUTTON_MAPPING } from "../devices/InputMapping";
import { inputService } from "../input/InputService";
assertIsWeb("ControllerModule");
const tmpVector31 = new Vector3();
const tmpVector32 = new Vector3();
const tmpVector33 = new Vector3();
const tmpQuaternion1 = new Quaternion();
const tmpQuaternion2 = new Quaternion();
const GRAB_BUTTON_DESCRIPTIONS = { key: "gestureMove" };
// Ray/grip space: what the selection ray and picked-up objects are currently attached to
// (targetRaySpace-derived pose - see ControllerInputDevice.updateTransform()'s worldPosition/worldOrientation).
const CONTROLLER_DEBUG_AXIS_HELPER_SIZE = 0.1;
// Visual/wrist space: the wrist-joint-corrected pose that already drives the hand collision model and is closer
// to the rendered hand mesh (see ControllerInputDevice.updateTransform()'s visualPosition/visualOrientation).
// Kept smaller than the ray/grip helper above so both remain distinguishable when overlapping.
const CONTROLLER_DEBUG_VISUAL_AXIS_HELPER_SIZE = 0.06;
/**
 * Manages the local player's mouse, keyboard and XR controller devices so input hardware is turned into tracked controller models, button descriptions and selection behaviour.
 *
 * Facets:
 * - `CustomControllerFacet` - Tracks externally managed controller models so they are updated like built-in local controllers; the tracking entry is removed again once the facet or its sub-model is removed.
 *
 * Messages:
 * - `EnableInputDevicesMessage`, `ChangeInputDeviceButtonMappingMessage`, `WebXRPresentingMessage` and `EndSessionMessage` - Enable/disable input devices, remap buttons or axes, flush pending controller updates around XR presentation changes, and reset button descriptions/recording state when a session ends.
 * - `ControllerHapticFeedback`, `SetControllerButtonDescription` and `ResetControllerButtonDescription` - Trigger controller haptics and set or restore the localized button description shown in controller annotations.
 * - `EnableControllerAnnotationMessage`, `ControllerVisibilityMessage`, `ControllerSelectionRayVisibilityMessage` and `EnableControllerJoystickMessage` - Toggle visible controller features such as button labels, controller meshes, selection rays and joysticks.
 * - `SetSelectionStrategyForHandsMessage` - Sets which selection strategy (ray, proximity or intent select) the controller(s) on given hands should use.
 * - `AudioVolumeMessage` - Updates each connected controller's `RecordingIndicator` facet with the current input volume while recording is active.
 * - `RequestControllerTransformMessage` - Answers with the current world transform of every connected controller and the platform scale.
 */
export class ControllerModule extends Module {
    /**
     * Registers all controller-related bus subscriptions (button/axis messages, facet tracking, visibility and
     * selection-strategy changes) and resets the module's runtime state via `onReset`.
     */
    constructor() {
        super(ModuleId.CONTROLLER);
        // we need a second connection for the input device messages,
        // so we can receive ControllerButtonMessage via our main connection.
        this.inputDeviceConnection = busService.connection("InputDevice");
        this.enableInputDevices = false;
        /** The currently connected controllers. */
        this.controllers = new Map();
        this.buttonDescriptions = new Map();
        this.showControllerButtonInfos = true;
        this.isShowingVrController = true;
        this.isShowingSelection = true;
        this.showControllerAxisHelpers = false;
        this.recording = false;
        // TODO: Ugly hack, it would be nicer to have some information that always exist per controller type and some
        //  other data that represents the currently connected controller
        this.staticControllerInfos = new Map();
        this.pendingMessages = new PendingMessages();
        this.handControllerStrategy = new Map();
        this.customControllers = new Map();
        this.pendingSelectionStrategyResync = new Set();
        this.inputDevicesInitialized = false;
        // TODO: HACK changed values for the PTS demo. this is a bit ugly, this might ideally happen per controller type
        this.leftControllerRayOrientation = new Quaternion(-0.12, -0.1, 0, 1);
        this.useMetaStreaming = true;
        this.update = (xrFrame) => {
            var _a, _b;
            this.ensureInputDevicesInitialized();
            gamepadDetectionService.update(xrFrame);
            this.processPendingSelectionStrategyResync();
            (_a = this.mouse) === null || _a === void 0 ? void 0 : _a.update();
            (_b = this.keyboard) === null || _b === void 0 ? void 0 : _b.update();
            for (const controller of this.controllers.values()) {
                controller.update();
            }
            this.flushPendingMessages();
        };
        this.updateControllerWorldTranforms = () => {
            this.ensureInputDevicesInitialized();
            for (const controller of this.controllers.values()) {
                controller.updateTransform();
            }
            this.flushPendingMessages();
        };
        this.handleWebXRPresentingMessage = (_msg) => {
            this.flushPendingMessages();
        };
        this.onControllerConnected = (controllerInfo) => {
            const controllerUid = this.ensureControllerUid(controllerInfo);
            if (this.controllers.get(controllerUid))
                return;
            controllerInfo.uid = controllerUid;
            const staticControllerInfo = this.getOrCreateStaticControllerInfo(controllerInfo.hand);
            controllerInfo.joystickEnabled = staticControllerInfo.joystickEnabled;
            console.debug(`Connected Controller: ${JSON.stringify(controllerInfo)}`);
            const controller = this.createControllerDevice(controllerUid, controllerInfo);
            // Calculate new rayOrigin and rayDirection based on measured data
            this.changeRaycastOriginAndDirection(controllerInfo, controller.hand);
            this.pendingMessages.push(new ControllerConnectedMessage(controllerUid, controllerInfo.type, controller.hand, convertToNumber3(controllerInfo.rayOrigin), convertToNumber3(tmpVector31.set(0, 0, -1).applyQuaternion(controllerInfo.rayRotation)), controllerInfo.hasTrackedHand));
            // Created before createControllerModel() so updateControllerAxisHelper() (called from within it) can already
            // find the hand collision model if axis helpers are toggled on.
            controller.createHandCollisionModel(controller.hand);
            this.createControllerModel(controller);
        };
        /**
         * Called when an existing controller switches between physical-controller mode and
         * hand-tracking mode (i.e. hasTrackedHand flipped).  We must update the legacy
         * hand/controller mesh visibility without tearing down and re-creating the controller.
         */
        this.onTrackedHandChanged = (controllerInfo) => {
            const controllerId = this.getControllerUid(controllerInfo);
            if (controllerId === undefined)
                return;
            const controller = this.controllers.get(controllerId);
            if (!(controller === null || controller === void 0 ? void 0 : controller.view))
                return;
            // Update hasTrackedHand on the view – this suppresses the static hand mesh when
            // hand-tracking is active, and re-enables it when switching back to controllers.
            controller.view.setHasTrackedHand(controllerInfo.hasTrackedHand, this.pendingMessages);
            // Defer SelectionStrategy ray refresh to the next input frame, after
            // GamepadDetectionService has written the current rayOrigin/rayRotation.
            this.pendingSelectionStrategyResync.add(controllerId);
            // Broadcast the tracking-mode change so peer instances can update their display.
            this.pendingMessages.push(new ControllerTrackingChangedMessage(controllerId, controllerInfo.hasTrackedHand));
            this.flushPendingMessages();
            console.debug(`[controller] hasTrackedHand changed → ${String(controllerInfo.hasTrackedHand)}`);
        };
        this.onControllerDisconnected = (controllerInfo) => {
            const controllerId = this.getControllerUid(controllerInfo);
            if (controllerId === undefined) {
                return;
            }
            const controller = this.controllers.get(controllerId);
            if (!controller)
                return;
            this.pendingSelectionStrategyResync.delete(controllerId);
            this.removePendingMessagesForUid(controllerId);
            const handCollisionModelUid = controller.getHandCollisionModelUid();
            if (handCollisionModelUid !== undefined) {
                this.removePendingMessagesForUid(handCollisionModelUid);
            }
            controller.onDisconnect();
            controller.deleteHandCollisionModel();
            this.pendingMessages.push(new ControllerDisconnectedMessage(controllerId));
            this.controllers.delete(controllerId);
            this.flushPendingMessages();
            console.debug(`Disconnected Controller: ${controller.uid}`);
        };
        this.handleSetSelectionStrategyByHandMessage = (msg) => {
            for (const hand of msg.hands) {
                this.handControllerStrategy.set(hand, msg.strategy);
            }
            this.updateSelectionStrategyForHands(msg.hands, msg.strategy);
        };
        this.handleCustomControllerFacet = (uid, facet, meta) => {
            const model = modelPool.getModel(uid);
            if (model === undefined)
                return;
            if (model.getRoomOrThrow().getUID() === ROOT_MODEL_UID)
                return;
            this.customControllers.set(uid, {
                updatedTime: meta.timeStamp,
                uid,
                id: facet.id
            });
        };
        this.handleRemoveFacetFromModelMessage = (msg, meta) => {
            const model = this.customControllers.get(msg.uid);
            if (model === undefined)
                return;
            if (model.updatedTime > meta.timeStamp)
                return;
            this.customControllers.delete(msg.uid);
        };
        this.handleRemoveSubModelMessage = (msg, meta) => {
            const model = this.customControllers.get(msg.uid);
            if (model === undefined)
                return;
            if (model.updatedTime > meta.timeStamp)
                return;
            this.customControllers.delete(msg.uid);
        };
        this.handleControllerVisibilityMessage = (msg) => {
            this.isShowingVrController = msg.visible;
            for (const controller of this.controllers.values()) {
                if (!controller.view)
                    continue;
                // Only update the controller-shape (isShowingVRController); hasTrackedHand is unchanged.
                controller.view.showVrController(this.isShowingVrController, this.pendingMessages);
            }
            this.flushPendingMessages();
        };
        this.handleEnableControllerJoystickMessage = (msg) => {
            const staticControllerInfo = this.getOrCreateStaticControllerInfo(msg.hand);
            staticControllerInfo.joystickEnabled = msg.enable;
            // Update controller info
            gamepadDetectionService.getControllerInformationByHandedness(msg.hand).forEach((controllerInfo) => {
                controllerInfo.joystickEnabled = msg.enable;
            });
            // Update controllers
            this.getControllersByHand(msg.hand).forEach((controller) => {
                controller.joystickEnabled = msg.enable;
            });
        };
        this.handleControllerSelectionVisibilityMessage = (msg) => {
            if (msg.controllerUid !== undefined) {
                const controller = this.controllers.get(msg.controllerUid);
                if (!controller)
                    return;
                if (!controller.view)
                    return;
                controller.view.showSelection(msg.visible, this.pendingMessages);
                this.flushPendingMessages();
                return;
            }
            this.isShowingSelection = msg.visible;
            for (const controller of this.controllers.values()) {
                if (!controller.view)
                    continue;
                controller.view.showSelection(msg.visible, this.pendingMessages);
            }
            this.flushPendingMessages();
        };
        this.connection.subscribeByType(SetSelectionStrategyForHandsMessage, this.handleSetSelectionStrategyByHandMessage);
        this.connection.subscribe([
            ControllerHapticFeedback.typestatic,
            SetControllerButtonDescription.typestatic,
            ResetControllerButtonDescription.typestatic,
            AudioVolumeMessage.typestatic,
            ChangeInputDeviceButtonMappingMessage.typestatic,
            EnableInputDevicesMessage.typestatic,
            EndSessionMessage.typestatic,
            RequestControllerTransformMessage.typestatic,
            EnableControllerAnnotationMessage.typestatic
        ], (msg) => {
            this.handleMessage(msg);
        });
        this.connection.subscribeByType(WebXRPresentingMessage, this.handleWebXRPresentingMessage);
        this.connection.subscribeByFacetType(CustomControllerFacet, this.handleCustomControllerFacet);
        this.connection.subscribeByType(RemoveFacetFromModelMessage, this.handleRemoveFacetFromModelMessage, CustomControllerFacet.typestatic);
        this.connection.subscribeByType(RemoveSubModelMessage, this.handleRemoveSubModelMessage);
        this.connection.subscribeByType(ControllerVisibilityMessage, this.handleControllerVisibilityMessage);
        this.connection.subscribeByType(ControllerSelectionRayVisibilityMessage, this.handleControllerSelectionVisibilityMessage);
        this.connection.subscribeByType(EnableControllerJoystickMessage, this.handleEnableControllerJoystickMessage);
        this.onReset();
    }
    handleMessage(message) {
        if (ControllerHapticFeedback.is(message)) {
            this.onHapticFeedback(message);
        }
        else if (SetControllerButtonDescription.is(message)) {
            this.onSetControllerButtonDescription(message);
        }
        else if (ResetControllerButtonDescription.is(message)) {
            this.onResetControllerButtonDescription(message);
        }
        else if (AudioVolumeMessage.is(message)) {
            this.onAudioVolumeMessage(message);
        }
        else if (ChangeInputDeviceButtonMappingMessage.is(message)) {
            this.onChangeInputDeviceButtonMappingMessage(message);
        }
        else if (EnableInputDevicesMessage.is(message)) {
            this.setEnableInputDevices(message.enable);
        }
        else if (EnableControllerAnnotationMessage.is(message)) {
            this.showAnnotation(message.enable);
        }
        else if (AskMessage.is(message)) {
            const msg = message.message;
            if (RequestControllerTransformMessage.is(msg)) {
                this.onRequestControllerTransformMessage(message.askId);
            }
        }
        else if (EndSessionMessage.is(message)) {
            this.onReset();
        }
    }
    /**
     * Reads the preferred streaming mode, wires up gamepad connect/disconnect/hand-tracking callbacks, hooks the
     * per-frame input and controller-transform updates into the animation frame loop, and registers the
     * dev-only controller debug input actions (toggle button info, toggle axis helpers, calibrate, reset height).
     */
    init() {
        this.useMetaStreaming = preferencesService.getPreferredValue("streaming", "meta") === "meta";
        preferencesService.onPreferenceChanged.add((preferenceName, newValue) => __awaiter(this, void 0, void 0, function* () {
            if (preferenceName === "streaming") {
                this.useMetaStreaming = newValue === "meta";
            }
            return true;
        }));
        gamepadDetectionService.gamepadConnected$.add(this.onControllerConnected);
        gamepadDetectionService.gamepadDisconnected$.add(this.onControllerDisconnected);
        gamepadDetectionService.hasTrackedHandChanged$.add(this.onTrackedHandChanged);
        animationFrame$.onInputUpdate$.add(this.update);
        animationFrame$.add(this.updateControllerWorldTranforms);
        this.ensureInputDevicesInitialized();
        inputService
            .registerAction("ToggleControllerButtonInfo", () => {
            this.showAnnotation(!this.showControllerButtonInfos);
        }, "Activate VR Controller Info")
            .addKey("KeyI")
            .asDev();
        inputService
            .registerAction("ToggleControllerAxisHelpers", () => {
            this.toggleControllerAxisHelpers();
        }, "Toggle VR controller axis helpers")
            .addKey("KeyN")
            .asDev();
        inputService
            .registerAction("Calibrate", () => {
            this.calibrate();
        }, "Calibrate VR positioning")
            .addKey("KeyC")
            .asDev();
        inputService
            .registerAction("ResetHeight", () => {
            this.resetHeight();
        }, "Reset VR head height")
            .addKey("KeyR")
            .asDev();
    }
    /**
     * Resets button descriptions to their default (grab-only), clears the recording flag and flushes any
     * pending messages. Called on construction and whenever a session ends.
     */
    onReset() {
        this.buttonDescriptions.clear();
        this.buttonDescriptions.set(ButtonType.GRAB, GRAB_BUTTON_DESCRIPTIONS);
        this.recording = false;
        this.flushPendingMessages();
    }
    /**
     * Shows or hides the controller button annotations on every currently connected controller.
     */
    showAnnotation(visible) {
        this.showControllerButtonInfos = visible;
        for (const controller of this.controllers.values()) {
            const view = controller.view;
            view && view.setShowAnnotations(visible, this.pendingMessages);
        }
    }
    calibrate() {
        const midPos = tmpVector31.set(0, 0, 0);
        const midDir = tmpVector32.set(0, 0, 0);
        let num = 0;
        for (const ctr of this.controllers.values()) {
            tmpVector33.set(0, 0, -1).applyQuaternion(ctr.worldOrientation);
            midPos.add(ctr.worldPosition);
            midDir.add(tmpVector33);
            if (num++ > 0) {
                midPos.multiplyScalar(0.5);
                midDir.multiplyScalar(0.5);
            }
        }
        midDir.normalize();
        midPos.y = 0;
        const yaw = Math.atan2(midDir.x, midDir.z);
        this.connection.send(new UpdatePlatformTransformMessage({
            position: midPos.toArray(),
            angle: yaw,
            scale: 1
        }));
        this.connection.send(new SetSkyboxRotation(yaw));
    }
    resetHeight() {
        this.connection.send(new UpdatePlatformTransformMessage({ height: 0, scale: 1 }));
    }
    /**
     * Enables or disables the mouse, keyboard and all currently connected controllers.
     */
    setEnableInputDevices(enable) {
        this.enableInputDevices = enable;
        this.mouse && this.mouse.setEnabled(enable);
        this.keyboard && this.keyboard.setEnabled(enable);
        for (const ctr of this.controllers.values()) {
            ctr.setEnabled(enable);
        }
    }
    /**
     * Triggers a haptic pulse on the addressed controller's gamepad, using whichever vibration API
     * (`hapticActuators` or `vibrationActuator`) it supports.
     */
    onHapticFeedback(message) {
        if (!this.enableInputDevices)
            return;
        const controller = this.controllers.get(message.uid);
        if (!controller)
            return;
        const gamepad = controller.info.gamepad;
        if (!gamepad)
            return;
        if (!gamepad.hapticActuators)
            return;
        if (message.actuatorId < 0)
            return;
        if (message.actuatorId >= gamepad.hapticActuators.length)
            return;
        gamepad.hapticActuators[message.actuatorId].pulse(1.0, 200);
        if (gamepad.vibrationActuator) {
            ;
            gamepad.vibrationActuator.playEffect("dual-rumble", {
                startDelat: 0,
                duration: 1000,
                weakMagnitude: 1.0,
                strongMagnitude: 1.0
            });
        }
    }
    /**
     * Sets a button's localized description on a specific controller, or on all controllers and the stored
     * default when no controller is specified.
     */
    onSetControllerButtonDescription(message) {
        if (message.controllerUid !== undefined) {
            const controller = this.controllers.get(message.controllerUid);
            if (controller && controller.view) {
                controller.view.setButtonDescription(message.buttonType, message.description);
            }
        }
        else {
            this.buttonDescriptions.set(message.buttonType, message.description || "");
            for (const controller of this.controllers.values()) {
                if (controller.view) {
                    controller.view.setButtonDescription(message.buttonType, message.description);
                }
            }
        }
    }
    /**
     * Restores a controller's button description back to the stored default for that button type.
     */
    onResetControllerButtonDescription(message) {
        const controller = this.controllers.get(message.controllerUid);
        if (controller && controller.view) {
            controller.view.setButtonDescription(message.buttonType, this.buttonDescriptions.get(message.buttonType));
        }
    }
    /**
     * Applies a new button mapping to the addressed input device (mouse, keyboard, or all controllers of the
     * given type) and persists it in the shared `BUTTON_MAPPING` table.
     */
    onChangeInputDeviceButtonMappingMessage(msg) {
        if (msg.device === InputDeviceType.Mouse) {
            this.mouse && this.mouse.updateButtonMapping(msg.buttonID, msg.buttonType);
        }
        else if (msg.device === InputDeviceType.Keyboard) {
            this.keyboard && this.keyboard.updateButtonMapping(msg.buttonID, msg.buttonType);
        }
        else {
            for (const controller of this.controllers.values()) {
                if (controller.type === msg.device) {
                    controller.updateButtonMapping(msg.buttonID, msg.buttonType);
                }
            }
        }
        const mapping = (BUTTON_MAPPING[msg.device] = BUTTON_MAPPING[msg.device] || {});
        if (msg.buttonType !== undefined) {
            mapping[msg.buttonID] = msg.buttonType;
        }
        else {
            delete mapping[msg.buttonID];
        }
    }
    /**
     * Returns all currently connected controllers tracked on the given hand.
     */
    getControllersByHand(hand) {
        return Array.from(this.controllers.values()).filter((controller) => controller.hand === hand);
    }
    onRequestControllerTransformMessage(askId) {
        const controllers = [];
        for (const controller of this.controllers.values()) {
            controllers.push({
                uid: controller.uid,
                position: convertToNumber3(controller.worldPosition),
                orientation: convertToNumber4(controller.worldOrientation)
            });
        }
        this.connection.send(new ResponseControllerTransformMessage(controllers, localPlayerContainer.getPlatformScale()), askId);
    }
    /**
     * Sends all currently queued controller/input messages on the dedicated input-device connection, once the
     * connection is ready, and clears the queue.
     */
    flushPendingMessages() {
        if (!this.connection.isReady())
            return;
        if (this.pendingMessages.isEmpty())
            return;
        this.inputDeviceConnection.sendMultiple(this.pendingMessages.get());
        this.pendingMessages.clear();
    }
    ensureControllerUid(controllerInfo) {
        var _a;
        const controllerRoot = ensureLocalPlayerControllersModel();
        const controllerName = getPlayerControllerModelName(getLocalPlayerClientIdxOrThrow(), controllerInfo.hand);
        const controller = (_a = controllerRoot.getChild(controllerName)) !== null && _a !== void 0 ? _a : modelPool.createModel(controllerName, controllerRoot, useExternalMessages(this.pendingMessages));
        return controller.getUID();
    }
    getControllerUid(controllerInfo) {
        var _a;
        if (controllerInfo.uid !== undefined) {
            return controllerInfo.uid;
        }
        const controllerRoot = ensureLocalPlayerControllersModel();
        const controllerName = getPlayerControllerModelName(getLocalPlayerClientIdxOrThrow(), controllerInfo.hand);
        return (_a = controllerRoot.getChild(controllerName)) === null || _a === void 0 ? void 0 : _a.getUID();
    }
    ensureInputDevicesInitialized() {
        if (this.inputDevicesInitialized || !hasLocalPlayerClientIdx()) {
            return;
        }
        const controllerRoot = ensureLocalPlayerControllersModel();
        const mouseDeviceId = getPlayerMouseModelName(getLocalPlayerClientIdxOrThrow());
        const props = useExternalMessages(this.pendingMessages);
        const mouseDevice = modelPool.createModel(mouseDeviceId, controllerRoot, props);
        controllerDeviceService.mouseDeviceUID = mouseDevice.getUID();
        this.pendingMessages.push(new MouseConnectedMessage(mouseDevice.getUID()));
        this.mouse = new MouseInputDevice(mouseDevice.getUID(), this.pendingMessages, this.enableInputDevices);
        this.mouse.createModel();
        const cachedMouseStrategy = this.handControllerStrategy.get(ControllerHand.RightHand);
        if (cachedMouseStrategy !== undefined) {
            this.pendingMessages.push(new SetSelectionStrategyMessage(mouseDevice.getUID(), [0, 0, 0], [0, 0, 0, 1], cachedMouseStrategy));
        }
        this.updateMouseAxisHelper();
        this.keyboard = new KeyboardInputDevice(mouseDevice.getUID(), this.pendingMessages, this.enableInputDevices);
        this.inputDevicesInitialized = true;
        this.flushPendingMessages();
    }
    /**
     * Nudges a controller's ray origin/rotation by a fixed per-hand offset to compensate for the Meta streaming
     * controller pose, unless a tracked hand is being used or streaming compensation is disabled.
     */
    // TODO: HACK changed values for the PTS demo. this is a bit ugly
    changeRaycastOriginAndDirection(controllerInfo, hand = controllerInfo.hand) {
        if (!this.useMetaStreaming)
            return;
        if (controllerInfo.hasTrackedHand)
            return;
        if (hand === ControllerHand.LeftHand) {
            controllerInfo.rayOrigin.add(tmpVector31.set(-0.03, 0.07, 0.15));
            // controllerInfo.rayRotation.multiply(new Quaternion(0.165, 0.075, 0, 1))
            controllerInfo.rayRotation.multiply(this.leftControllerRayOrientation);
        }
        else if (hand === ControllerHand.RightHand) {
            controllerInfo.rayOrigin.add(tmpVector31.set(0.03, 0.07, 0.15));
            // controllerInfo.rayRotation.multiply(new Quaternion(0.165, -0.075, 0, 1))
            const l = this.leftControllerRayOrientation;
            const rightControllerRayOrientation = new Quaternion(l.x, -l.y, l.z, l.w);
            controllerInfo.rayRotation.multiply(rightControllerRayOrientation);
        }
    }
    createControllerDevice(controllerUid, controllerInfo) {
        const decidedHand = this.decideControllerHand(controllerInfo);
        const user = usersService.getLocalUser();
        const controller = new ControllerInputDevice(this, controllerInfo, controllerUid, this.pendingMessages, this.enableInputDevices, decidedHand, user ? user.avatar.baseColor : 0);
        controller.joystickEnabled = controllerInfo.joystickEnabled;
        this.controllers.set(controllerUid, controller);
        return controller;
    }
    createControllerModel(controller) {
        const selectionStrategy = this.handControllerStrategy.get(controller.hand);
        // isShowingVrController controls the controller shape mesh only.
        // hasTrackedHand suppresses all legacy meshes when hand-tracking is active.
        controller.createModel(this.isShowingVrController, this.showControllerButtonInfos, selectionStrategy, controller.info.hasTrackedHand);
        this.updateControllerAxisHelper(controller);
        for (const [type, desc] of this.buttonDescriptions) {
            controller.view && controller.view.setButtonDescription(type, desc);
        }
        if (!this.isShowingSelection && controller.view) {
            controller.view.showSelection(this.isShowingSelection, this.pendingMessages);
        }
        if (this.recording) {
            this.pendingMessages.push(new UpdateFacetOnModelMessage(controller.uid, new RecordingIndicator(0)));
        }
    }
    toggleControllerAxisHelpers() {
        this.showControllerAxisHelpers = !this.showControllerAxisHelpers;
        for (const controller of this.controllers.values()) {
            this.updateControllerAxisHelper(controller);
        }
        this.updateMouseAxisHelper();
        this.flushPendingMessages();
    }
    updateControllerAxisHelper(controller) {
        const gripSpaceModel = getControllerGripSpaceModel(controller.uid);
        if (gripSpaceModel !== undefined) {
            if (this.showControllerAxisHelpers) {
                this.pendingMessages.push(new UpdateFacetOnModelMessage(gripSpaceModel.getUID(), new AxisHelperFacet(CONTROLLER_DEBUG_AXIS_HELPER_SIZE)));
            }
            else {
                this.pendingMessages.push(new RemoveFacetFromModelMessage(gripSpaceModel.getUID(), AxisHelperFacet));
            }
        }
        // Second, smaller helper on the hand collision model to compare against the wrist/visual pose.
        const handCollisionModelUid = controller.getHandCollisionModelUid();
        if (handCollisionModelUid !== undefined) {
            if (this.showControllerAxisHelpers) {
                this.pendingMessages.push(new UpdateFacetOnModelMessage(handCollisionModelUid, new AxisHelperFacet(CONTROLLER_DEBUG_VISUAL_AXIS_HELPER_SIZE)));
            }
            else {
                this.pendingMessages.push(new RemoveFacetFromModelMessage(handCollisionModelUid, AxisHelperFacet));
            }
        }
    }
    updateMouseAxisHelper() {
        if (this.mouse === undefined)
            return;
        if (this.showControllerAxisHelpers) {
            this.pendingMessages.push(new UpdateFacetOnModelMessage(this.mouse.uid, new AxisHelperFacet(CONTROLLER_DEBUG_AXIS_HELPER_SIZE)));
        }
        else {
            this.pendingMessages.push(new RemoveFacetFromModelMessage(this.mouse.uid, AxisHelperFacet));
        }
    }
    /**
     * Makes sure the controller.hand is not neutral.
     *
     * Do use this _before_ adding the controller to this.connectedControllers!
     */
    decideControllerHand(info) {
        var _a;
        if (info.hand !== ControllerHand.Unknown) {
            return info.hand;
        }
        if (this.controllers.size === 0) {
            return ControllerHand.RightHand;
        }
        if (this.controllers.size > 1) {
            console.warn("Two or more controllers are already connected, and an additional one (of unknown side) is being added. Will take the side opposite to the first one!");
        }
        const handOfFirstController = (_a = this.controllers.values().next().value) === null || _a === void 0 ? void 0 : _a.hand;
        return handOfFirstController === ControllerHand.LeftHand ? ControllerHand.RightHand : ControllerHand.LeftHand;
    }
    processPendingSelectionStrategyResync() {
        if (this.pendingSelectionStrategyResync.size === 0)
            return;
        for (const controllerUid of this.pendingSelectionStrategyResync) {
            const controller = this.controllers.get(controllerUid);
            if (controller === undefined)
                continue;
            this.pushSelectionStrategyUpdate(controllerUid, controller);
        }
        this.pendingSelectionStrategyResync.clear();
    }
    pushSelectionStrategyUpdate(controllerUid, controller) {
        const strategy = this.resolveSelectionStrategy(controller);
        if (strategy === undefined)
            return;
        const rayOrigin = tmpVector31.copy(controller.info.rayOrigin);
        const rayRotation = tmpQuaternion1.copy(controller.info.rayRotation);
        if (this.useMetaStreaming && !controller.info.hasTrackedHand) {
            if (controller.hand === ControllerHand.LeftHand) {
                rayOrigin.add(tmpVector32.set(-0.03, 0.07, 0.15));
                rayRotation.multiply(this.leftControllerRayOrientation);
            }
            else if (controller.hand === ControllerHand.RightHand) {
                rayOrigin.add(tmpVector32.set(0.03, 0.07, 0.15));
                const l = this.leftControllerRayOrientation;
                rayRotation.multiply(tmpQuaternion2.set(l.x, -l.y, l.z, l.w));
            }
        }
        this.pendingMessages.push(new SetSelectionStrategyMessage(controllerUid, convertToNumber3(rayOrigin), convertToNumber4(rayRotation), strategy));
    }
    resolveSelectionStrategy(controller) {
        const handStrategy = this.handControllerStrategy.get(controller.hand);
        if (handStrategy !== undefined) {
            return handStrategy;
        }
        const mesh = getControllerMeshInfo(controller.type, controller.hand);
        if (mesh === undefined)
            return undefined;
        const { strategy, params } = mesh.selection;
        switch (strategy) {
            case SELECTION_STRATEGY.RAY_SELECT:
                return {
                    type: strategy,
                    maxLength: params.max_ray_length,
                    visible: params.ray_visible,
                    color: params.ray_color,
                    headLength: params.ray_head_length,
                    headWidth: params.ray_head_width
                };
            case SELECTION_STRATEGY.PROXIMITY_SELECT:
                return {
                    type: strategy,
                    maxLength: params.max_distance
                };
            case SELECTION_STRATEGY.INTEN_SELECT:
                return {
                    type: strategy,
                    snappiness: params.snappiness,
                    stickiness: params.stickiness,
                    threshold: params.threshold,
                    coneHeight: params.cone_hight,
                    coneAngle: params.cone_angle
                };
            default:
                return undefined;
        }
    }
    removePendingMessagesForUid(uid) {
        const remainingMessages = this.pendingMessages.get().filter(message => {
            if ("uid" in message && message.uid === uid)
                return false;
            if ("controllerUid" in message && message.controllerUid === uid)
                return false;
            return true;
        });
        this.pendingMessages.clear();
        this.pendingMessages.push(...remainingMessages);
    }
    /**
     * While recording is active, updates every connected controller's `RecordingIndicator` facet with the
     * current input volume.
     */
    onAudioVolumeMessage(msg) {
        if (this.recording) {
            for (const uid of this.controllers.keys()) {
                this.pendingMessages.push(new UpdateFacetOnModelMessage(uid, new RecordingIndicator(msg.volume)));
            }
        }
    }
    updateSelectionStrategyForHands(hands, strategy) {
        const messages = [];
        for (const [uid, controller] of this.controllers) {
            if (!hands.includes(controller.hand))
                continue;
            messages.push(new SetSelectionStrategyMessage(uid, convertToNumber3(controller.info.rayOrigin), convertToNumber4(controller.info.rayRotation), strategy));
        }
        if (messages.length > 0) {
            this.connection.sendMultiple(messages);
        }
    }
    /// Static controller info helper methods ///
    getOrCreateStaticControllerInfo(hand) {
        let info = this.staticControllerInfos.get(hand);
        if (info === undefined) {
            info = {
                joystickEnabled: true
            };
            this.staticControllerInfos.set(hand, info);
        }
        return info;
    }
}
